/* W110 — WHEN A CLIENT'S SET NOTE SHOWS (his report, 2026-09-30 ~17:05: "only show when hovering over and hide when not.
 * on the mobile version … it will just be stuck and not hide again when we tap somewhere else"). SUPERSEDES W93's pin
 * ("a click pins it open until a second click or Escape"): a click pins NOTHING now.
 *
 * ONE pure rule, so the component asks it for everything and a test can drive every path:
 *   • a MOUSE or PEN (a device with hover): shown while the pointer is over the icon or the note, hidden when it leaves;
 *     a click changes nothing, so nothing is left behind.
 *   • a FINGER (no hover): a tap on the icon toggles it; a tap anywhere else (`outside`), Escape or a scroll hides it.
 *     A finger's enter/leave means nothing (the finger is lifted, not moved away).
 *   • the KEYBOARD: focus that the browser draws as focus (`:focus-visible`) shows it; blur or Escape hides it.
 * `open` is what `aria-expanded` says, so the attribute tells the truth in every state.
 * ⚠️ `by` records WHAT opened it, because a blur must not close what the pointer is still holding open (a mouse click focuses
 * the button), and a mouse leave must not close what a finger or the keyboard opened. */

export type NotePointer = "mouse" | "pen" | "touch" | string;

export type NoteState = { readonly open: boolean; readonly by: "hover" | "tap" | "focus" | null };

export type NoteEvent =
  | { type: "enter"; pointer: NotePointer }
  | { type: "leave"; pointer: NotePointer }
  | { type: "click"; pointer: NotePointer }
  | { type: "focus"; visible: boolean }
  | { type: "blur" }
  | { type: "escape" }
  | { type: "outside" }
  | { type: "scroll" };

export const NOTE_CLOSED: NoteState = { open: false, by: null };

const isFinger = (p: NotePointer) => p === "touch";

export function noteReducer(s: NoteState, e: NoteEvent): NoteState {
  switch (e.type) {
    case "enter":
      if (isFinger(e.pointer)) return s;
      return s.open ? s : { open: true, by: "hover" };
    case "leave":
      if (isFinger(e.pointer)) return s;
      return s.by === "hover" ? NOTE_CLOSED : s;
    case "click":
      // A mouse or pen click pins NOTHING (his word, superseding W93's pin); a finger's tap toggles.
      if (!isFinger(e.pointer)) return s;
      return s.open ? NOTE_CLOSED : { open: true, by: "tap" };
    case "focus":
      return e.visible && !s.open ? { open: true, by: "focus" } : s;
    case "blur":
      return s.by === "focus" || s.by === "tap" ? NOTE_CLOSED : s;
    case "escape":
    case "outside":
    case "scroll":
      return NOTE_CLOSED;
  }
}
